/**
 * #11a — WHICH engines Schema Compare wraps a sync script in a single
 * transaction by default.
 *
 * ## Why this is a module and not a constant in the tab
 *
 * The answer has to be given twice, in two processes, by code that can never
 * import each other:
 *
 *  - the renderer (`SchemaCompareTab`) uses it for the checkbox's default, for
 *    the second gate in `handleGenerateScript`, and for whether the control is
 *    rendered at all;
 *  - the main process (`electron/mcp-compare/schemaCompare.ts`) uses it for
 *    `schema_compare_generate_script`, whose stated contract is "mirror the
 *    Generate Script UI".
 *
 * #98 wrote that contract as the literal expression `engine === 'mssql'`,
 * repeated on both sides. #11a added PostgreSQL to the UI's side and the MCP
 * tool kept its own copy — so for the length of that change an agent and a
 * human performing the same action on the same PostgreSQL comparison got
 * different scripts, one atomic and one not, with nothing in either surface
 * saying so. A duplicated policy that MUST agree is the defect; this module is
 * the single place it is written down.
 *
 * It lives under `src/` rather than `electron/` because that is this repo's
 * established direction for renderer/main shared logic: the renderer never
 * imports electron runtime values (only ambient types via `src/types/*.d.ts`),
 * while `electron/schema-compare` and `electron/mcp-compare` already import a
 * dozen pure modules out of this very folder (`declaredScope`,
 * `scriptableKeys`, `wholeTargetDrop`, `schemaFetchProgressDisplay`, …).
 * `engineUtils.ts` next door is NOT a candidate home despite the name — it
 * reaches into the zustand connection store and so cannot be loaded in the
 * main process.
 *
 * ## Why these two, and only these two
 *
 * The list is "engines where the wrap is genuinely all-or-nothing through this
 * product", which is the same question `transactionWrapCaveat` answers by
 * returning `undefined` (`electron/schema-compare/transactionWrap.ts`). Those
 * two are pinned to each other by a test in `schemaScriptGenerator.test.ts`, so
 * an engine cannot be added here without the generator agreeing that its wrap
 * needs no caveat.
 *
 *  - `mssql` — verified end-to-end through sqlcmd; the emitted NOEXEC guard is
 *    what makes it hold in runners that continue past errors. One disclosed
 *    exception (a syntax error aborts only its own batch).
 *  - `pgsql` — DDL is transactional; a failure anywhere rolls the whole script
 *    back, PostgreSQL refuses later statements itself (25P02) and the trailing
 *    COMMIT on an aborted transaction acts as a ROLLBACK. The one defect that
 *    justified withholding it — a failed script stranding the pooled
 *    connection in an aborted transaction — was fixed in #53.
 *  - `mysql` / `oracle` commit implicitly around every DDL statement, so no
 *    surrounding transaction could roll their scripts back even in principle.
 *  - `sqlite` does support transactional DDL, but rollback for this script
 *    shape has never been verified through Jam SQL Studio's own executor.
 *  - `kusto` has no DDL sync script at all, and `mock` is a test engine.
 *
 * The four excluded engines still HONOUR an explicit `wrapInTransaction: true`
 * — they emit the wrap and raise a caveat saying what it does and does not
 * guarantee. This list is about the default and the affordance, never a
 * refusal.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

/**
 * Engines where "Wrap in a single transaction" is offered AND defaults on.
 *
 * ONE list, read by the renderer's three sites (default, generate gate, control
 * visibility) and by the MCP generator's default. Keeping them literally the
 * same expression is the invariant #98 wrote down and #11a makes structural — a
 * checkbox hidden on an engine that nevertheless defaults to ON would hand that
 * user a wrapped script with no way to see or undo the choice.
 */
export const WRAP_CAPABLE_ENGINES: readonly DatabaseEngineId[] = ['mssql', 'pgsql'];

/**
 * Whether Schema Compare offers, and defaults ON, the transaction wrap for this
 * engine.
 *
 * `undefined` answers `false` deliberately: a caller that does not yet know its
 * engine must not silently get the wrap. Both call sites can reach that state —
 * an MCP operation whose engine was never determined, and a tab whose target
 * connection has not resolved — and defaulting an unknown engine to "wrapped"
 * is exactly the unconditional default W1.5/F06 was raised to remove.
 */
export function engineWrapsInTransaction(engine: DatabaseEngineId | undefined): boolean {
  return engine !== undefined && WRAP_CAPABLE_ENGINES.includes(engine);
}
